/* eslint-disable react/prop-types */
import {
  Box,
  Flex,
  Stack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from '@chakra-ui/react';
import BEIcon from '@molecules/BEIcon';
import Text from '@molecules/Text';
import CheckInOut from './components/CheckInOut';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import BankTransferInstructions from './components/BankTransferInstructions';
import RedirectNotice from './components/RedirectNotice';
import { observer } from 'mobx-react';
import { useStore } from '@data/other/withStore';
import { useCallback, useMemo } from 'react';

import BEImage from '@molecules/BEImage';
import BEPills from '@molecules/BEPills';
import { ReservationAccommodationInfo } from '@data/models/Reservation';
import useFormat from '@business/utils/hooks/useFormat';
import Card from '@molecules/Card';

const StyledData = styled(Flex)`
  & .check-in-out {
    margin-top: 32px;
  }

  & .green-color {
    color: #32C0A0;
  }

  .text-info {
    margin-top: 12px;
    margin-bottom: 6px;
  }
`;

const PurchasedItem = observer((props: ReservationAccommodationInfo) => {
  const {
    property: { accommodations },
  } = useStore();
  const { currency } = useFormat();

  const title = useMemo(
    () => accommodations.getAccommodationName(props.id, props.packageId),
    [accommodations, accommodations.types, props.id, props.packageId]
  );
  const images = useMemo(
    () => accommodations.getAccommodationImages(props.id, props.packageId),
    [accommodations, accommodations.types, props.id, props.packageId]
  );

  return (
    <Flex gap="24px">
      <BEImage
        width="160px"
        height="105px"
        fit="cover"
        src={images[0]?.src}
        alt={images[0]?.src ?? ''}
        borderRadius="8px"
      />
      <Box flex={1}>
        <Flex justifyContent="space-between">
          <Text type="h3">{title}</Text>
          <Flex gap="8px">
            <Text type="h3">{currency(props.total)}</Text>
          </Flex>
        </Flex>
        <Box className="box">
          <Text type="h4" weight="400">
            {props.packageName}
          </Text>
          <Flex align="center" mt={2} gap={2}>
            {props.adults > 0 && (
              <BEPills iconRight="user">{props.adults}</BEPills>
            )}
            {props.kids > 0 && (
              <BEPills iconRight="child-reaching">{props.kids}</BEPills>
            )}
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
});

const Confirmed = () => {
  const { t } = useTranslation();
  const { property } = useStore();
  const { reservation } = property;
  const { redirectAfterReservation, redirectDelay, redirectUrl } =
    property.model?.settings || {};

  const renderInvalidReservation = useCallback(() => {
    return <Text>Invalid Reservation</Text>;
  }, []);

  const localStorageKey = 'viewed-reservation-confirmations';

  const alreadyViewedConfirmation = useMemo(() => {
    const currentReservationId = reservation.model?.reservationId || '';
    const viewedReservations = [
      ...JSON.parse(localStorage.getItem(localStorageKey) || '[]'),
    ];
    const alreadyViewed = viewedReservations.includes(currentReservationId);

    if (!alreadyViewed) {
      localStorage.setItem(
        localStorageKey,
        JSON.stringify([...viewedReservations, currentReservationId])
      );
    }
    return alreadyViewed;
  }, [reservation]);

  const renderReservation = useCallback(() => {
    return (
      <div>
        {redirectAfterReservation &&
          redirectUrl &&
          redirectDelay !== undefined &&
          !alreadyViewedConfirmation && (
            <RedirectNotice seconds={redirectDelay} url={redirectUrl} />
          )}
        <StyledData direction="column" align="center">
          <BEIcon
            name="check"
            size="2xl"
            weight="solid"
            className="green-color"
          />
          <Text type="h1">{t('confirmation-page-title')}</Text>
          <Text className="text-info">
            {t('confirmation-page-reservation-number').replace(
              '$value',
              reservation.model?.reservationId?.toString() || '0'
            )}
          </Text>
          <Text>
            {t('confirmation-page-mail-confirmation').replace(
              '$value',
              reservation.model?.guests[0].email || '0'
            )}
          </Text>
          {reservation.model ? <CheckInOut model={reservation.model} /> : null}

          <Accordion
            mt="16px"
            allowToggle
            allowMultiple
            defaultIndex={[0, 1]}
            w="100%"
          >
            <AccordionItem
              w="100%"
              mt="25px"
              bg="transparent"
              borderRadius="16px"
              overflow="hidden"
            >
              {({ isExpanded }) => (
                <Box width="100%" borderWidth="1px" borderRadius="16px">
                  <AccordionButton
                    height="48px"
                    gap="10px"
                    borderRadius={isExpanded ? '16px 16px 0 0' : '16px'}
                    bg="#F8F9FA"
                    _hover={{ backgroundColor: '#F8F9FA' }}
                  >
                    <BEIcon name={isExpanded ? 'angle-down' : 'angle-right'} />
                    <Text type="h3" weight="500">
                      Accommodations
                    </Text>
                  </AccordionButton>
                  <AccordionPanel p="24px">
                    <Stack gap="28px">
                      {reservation.model?.accommodations.map((item, i) => (
                        <Box key={'item-' + i}>
                          <PurchasedItem {...item} />
                        </Box>
                      ))}
                    </Stack>
                  </AccordionPanel>
                </Box>
              )}
            </AccordionItem>
          </Accordion>
          {reservation.model?.paymentMethod === 'ebanking' && (
            <BankTransferInstructions
              instructions={property.model?.bankTransferInstructions?.getText()}
            />
          )}
        </StyledData>
      </div>
    );
  }, [reservation, reservation.model]);

  return (
    <Card>
      {!reservation.model ? renderInvalidReservation() : renderReservation()}
    </Card>
  );
};

export default observer(Confirmed);
